import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../helpers/api';
import { getSingleTag } from '../helpers/tags';

const TagsEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitDisabled, setSubmitDisabled] = useState(false);

    const [formData, setFormData] = useState({
        name: ''
    });

    useEffect(() => {
        if (id !== 'new') {
            fetchTag();
        } else {
            setIsLoading(false);
        }
    }, [id]);

    const fetchTag = async () => {
        try {
            const response = await api.get(`/tags/${id}`);
            setFormData({
                name: response.data.data.name
            });
        } catch (err) {
            console.error('Erreur lors du chargement du tag:', err);
            setError('Erreur lors du chargement du tag');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const stripSystemFields = (data) => {
        if (!data) return data;
        const fieldsToStrip = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt'];

        if (Array.isArray(data)) {
            return data.map(item => stripSystemFields(item));
        }

        if (typeof data === 'object') {
            const cleaned = {};
            for (const [key, value] of Object.entries(data)) {
                if (fieldsToStrip.includes(key)) continue;
                if (Array.isArray(value)) {
                    cleaned[key] = value.map(item =>
                        typeof item === 'object' && item !== null ? stripSystemFields(item) : item
                    );
                } else if (typeof value === 'object' && value !== null) {
                    cleaned[key] = stripSystemFields(value);
                } else {
                    cleaned[key] = value;
                }
            }
            return cleaned;
        }
        return data;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitDisabled(true);

        try {
            if (id === 'new') {
                // Create new tag
                await api.post('/tags', {
                    data: formData
                });
            } else {
                // Update existing tag
                await api.put(`/tags/${id}`, {
                    data: formData
                });
            }

            navigate('/tags', {
                state: {
                    message: `Tag ${id === 'new' ? 'créé' : 'modifié'} avec succès`
                }
            });
        } catch (err) {
            console.error('Erreur lors de la sauvegarde:', err);
            setError('Erreur lors de la sauvegarde');
            setSubmitDisabled(false);
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid px-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                    {id === 'new' ? 'Nouveau tag' : 'Modifier le tag'}
                </h2>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/tags')}
                >
                    Retour à la liste
                </button>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            <div className="card shadow-sm">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row mb-4">
                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label className="form-label">Nom</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate('/tags')}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={submitDisabled}
                            >
                                {submitDisabled ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Enregistrement...
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TagsEdit;